import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { STOCK } from "@/data/content";
import { useI18n } from "@/i18n";

export default function Activities() {
  const { t } = useI18n();
  const a = t.activities;
  const ACTIVITIES_INCLUDED = a.included;
  const ACTIVITIES_PAID = a.paid;
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [lightbox]);

  const Zoomable = ({ src, alt, testid, className }) => (
    <button
      type="button"
      onClick={() => setLightbox({ src, alt })}
      data-testid={testid}
      aria-label={`Powiększ zdjęcie: ${alt}`}
      className={`group overflow-hidden cursor-zoom-in ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
      />
    </button>
  );

  return (
    <section
      id="doswiadczenia"
      data-testid="activities-section"
      className="relative bg-canvas-surface py-14 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Intro — nagłówek po lewej, opis po prawej */}
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-end">
          <div className="col-span-12 md:col-span-6">
            <Reveal>
              <p className="overline mb-6">{a.overline}</p>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink">
                {a.headingA} <br />
                <span className="italic">{a.headingB}</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Reveal delay={0.15}>
              <p className="text-ink-muted text-base md:text-lg leading-relaxed max-w-md md:ml-auto">
                {a.intro}
              </p>
            </Reveal>
          </div>
        </div>

        {/* Oferujemy (lewa) + Dodatkowo płatne (prawa) — obok siebie */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <Reveal>
              <p className="overline text-brand-red mb-5">{a.includedLabel}</p>
            </Reveal>
            <Stagger className="space-y-4">
              {ACTIVITIES_INCLUDED.map((item) => (
                <StaggerItem key={item} className="flex items-baseline gap-4">
                  <span className="h-px w-6 bg-ink/40 translate-y-2 shrink-0" />
                  <span className="serif text-xl md:text-2xl font-light text-ink">
                    {item}
                  </span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          <div>
            <Reveal>
              <p className="overline text-brand-red mb-5">{a.paidLabel}</p>
            </Reveal>
            <Stagger className="space-y-4">
              {ACTIVITIES_PAID.map((p) => (
                <StaggerItem key={p.title} className="flex items-baseline gap-4">
                  <span className="h-px w-6 bg-brand-red/50 translate-y-2 shrink-0" />
                  <div>
                    <p className="serif text-xl md:text-2xl font-light text-ink leading-tight">
                      {p.title}
                    </p>
                    <p className="text-ink-muted text-sm leading-relaxed mt-1">
                      {p.desc}
                    </p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>

        {/* Rowery + śniadania */}
        <Reveal delay={0.15} className="mt-12 md:mt-16 block">
          <div className="grid grid-cols-12 gap-8 md:gap-16">
            <Zoomable
              src="/images/act-bikes-terrace.webp"
              alt="Rowery przy tarasie domku — wycieczki po okolicy"
              testid="activities-image-bikes-terrace"
              className="col-span-12 md:col-span-7 h-[320px] md:h-[460px]"
            />
            <Zoomable
              src="/images/breakfast-basket.jpg"
              alt="Śniadania ze swojskimi i lokalnymi produktami"
              testid="activities-image-breakfast"
              className="col-span-12 md:col-span-5 h-[320px] md:h-[460px]"
            />
          </div>
        </Reveal>

        {/* Rowery przy domku + gry planszowe */}
        <Reveal delay={0.15} className="mt-8 md:mt-16 block">
          <div className="grid grid-cols-12 gap-8 md:gap-16">
            <Zoomable
              src="/images/act-bikes-cottage.webp"
              alt="Rowery na trawie przy domku"
              testid="activities-image-bikes-cottage"
              className="col-span-12 md:col-span-5 h-[320px] md:h-[460px]"
            />
            <Zoomable
              src="/images/act-games.webp"
              alt="Gry planszowe w domku — Domino, Chińczyk, Monopoly Bydgoszcz"
              testid="activities-image-games"
              className="col-span-12 md:col-span-7 h-[320px] md:h-[460px]"
            />
          </div>
        </Reveal>

        {/* Nasza pasieka */}
        <Reveal delay={0.15} className="mt-8 md:mt-16 block">
          <Zoomable
            src="/images/apiary-1.webp"
            alt="Nasza pasieka — pszczelarz przy ulach"
            testid="activities-image-apiary-1"
            className="w-full aspect-[16/9]"
          />
        </Reveal>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="activities-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(null)}
          >
            <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm" />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              data-testid="activities-lightbox-close"
              aria-label="Zamknij"
              className="absolute top-6 right-6 z-10 text-canvas/80 hover:text-canvas transition-colors"
            >
              <X size={28} strokeWidth={1.4} />
            </button>
            <motion.img
              src={lightbox.src}
              alt={lightbox.alt}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 max-h-[90vh] max-w-[92vw] object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
